import { useState } from "react";
import Notify from "devextreme/ui/notify";
//DBTables
import { DBModule, ModuleTypeId } from "../../../types/DBTables";
import { UseFormReturn } from "react-hook-form";

import NumberEditor from "../../Global/Editors/NumberEditor";
import CheckboxEditor from "../../Global/Editors/CheckboxEditor";
import TextEditor from "../../Global/Editors/TextEditor";
import { useGlobalData } from "../../GlobalDataProvider";
import SelectEditor from "../../Global/Editors/SelectEditor";
import { DTOModule } from "../../../types/building";
import { IModuleRound, useRound } from "../../../modules/roundHandler";
import { nameof } from "../../Global/Utils";
import {
  IModuleVentilationFTXochFT,
  IModuleVentilationFTXochFTConfig,
  IModuleVentilationFTXochFTRound,
  IModuleVentilationRoundKanaler,
  IModuleVentilationRoundTidskanaler,
} from "../../../types/ModuleDatas";
import TidsKanalEditor from "./TidsKanalEditor";
import TimePickerEditor from "../../Global/Editors/TimePickerEditor";
import { WeekDayName } from "../../../types/module-type";
import notify from "devextreme/ui/notify";
import { MapTooltip } from "../../../types/globalData";
import { sumSchedule } from "../../../modules/TimePeriods";
import BFLNote from "../BFL/BFLNote";
import { logDebugSilently } from "../../../modules/logger";
import StaticBox from "../../Global/StaticBox";

interface IProps {
  module: IModuleRound;
  data: any;
  formHook: UseFormReturn;
}

const RoundVentilationFTX = ({ module, data, formHook }: IProps) => {
  const globalData = useGlobalData();
  const roundHandler = useRound();

  const moduleData = data as IModuleVentilationFTXochFT;

  const isFTX = module.moduleTypeId === ModuleTypeId.VentilationFTX;
  const hasKyla = moduleData.kylbatteri !== "Kyla saknas";

  const luftflödesUppgiftList = globalData.data?.Ventilation.LuftflödesUppgift.map((v) => v.LuftflödesUppgift) || [];
  const luftflödesEnhetList = globalData.data?.Ventilation.LuftflödesEnhet.map((v) => v.LuftflödesEnhet) || [];

  const rektStorlekList = globalData.data?.Ventilation.KanalstorlekRektangulära.map((v) => v.KanalstorlekRektangulära) || [];
  const cirkStorlekList = globalData.data?.Ventilation.KanalstorlekCirkulära.map((v) => v.KanalstorlekCirkulära) || [];

  const flödeUppgift = formHook.watch("flödeUppgift");
  const flödesKanalForm = formHook.watch("flödesKanalForm");
  const driftÅretRunt = formHook.watch("driftÅretRunt");

  const flödeUppgiftAsSchablon = flödeUppgift === "Uppgift saknas";

  const setChannelFromFromLastRound = (channelName: string) => {
    const lastRound = roundHandler.getLastFinishedRound();
    if (!lastRound) {
      notify("Hittar ingen tidigare klarmarkerad rondering", "error");
      return;
    }
    const thisModuleLastRound = lastRound.data.modules.find((m) => m.id === module.id);
    if (thisModuleLastRound) {
      const roundTidsKanaler = thisModuleLastRound.data.tidsKanaler;
      const bfsTidsKanaler = thisModuleLastRound.bfs?.BFL_J3_FTX_FT?.data?.tidsKanaler;

      const kanal = (bfsTidsKanaler ? bfsTidsKanaler[channelName] : undefined) || (roundTidsKanaler ? roundTidsKanaler[channelName] : undefined);
      logDebugSilently("Setting tidskanaler from last round....");
      if (kanal) formHook.setValue("tidsKanaler." + channelName, kanal);
    }
  };

  const setDaysFrom = (channelName: string, dayName: WeekDayName, appliesTo: WeekDayName[]) => {
    var data = formHook.getValues();
    var tidsKanaler = data.tidsKanaler;
    if (tidsKanaler) {
      const kanal = tidsKanaler[channelName];
      if (kanal) {
        const periods = kanal[dayName];
        if (periods) {
          appliesTo.forEach((wd) => {
            kanal[wd] = periods.slice();
          });
          formHook.setValue("tidsKanaler." + channelName, kanal);
        }
      }
    }
  };

  const tidsKanaler = [
    {
      channelPath: "tidsKanaler",
      fieldName: "ventilation",
      caption: "Ventilation",
      hasType: true,
      buttons: [
        { caption: "Hämta schema från föregående rondering", callback: (dayName: WeekDayName) => setChannelFromFromLastRound("ventilation") },
        {
          caption: "Applicera detta dagsschema för måndag-fredag",
          callback: (dayName: WeekDayName) =>
            setDaysFrom("ventilation", dayName, [WeekDayName.monday, WeekDayName.tuesday, WeekDayName.wednesday, WeekDayName.thursday, WeekDayName.friday]),
        },
        {
          caption: "Applicera detta dagsschema för lördag-söndag",
          callback: (dayName: WeekDayName) => setDaysFrom("ventilation", dayName, [WeekDayName.saturday, WeekDayName.sunday]),
        },
        {
          caption: "Applicera detta dagsschema för måndag-söndag",
          callback: (dayName: WeekDayName) =>
            setDaysFrom("ventilation", dayName, [
              WeekDayName.monday,
              WeekDayName.tuesday,
              WeekDayName.wednesday,
              WeekDayName.thursday,
              WeekDayName.friday,
              WeekDayName.saturday,
              WeekDayName.sunday,
            ]),
        },
      ],
    },
    {
      channelPath: "tidsKanaler",
      fieldName: "närvaro",
      caption: "Närvaro",
      hasType: false,
      buttons: [
        { caption: "Hämta schema från föregående rondering", callback: (dayName: WeekDayName) => setChannelFromFromLastRound("närvaro") },
        {
          caption: "Applicera detta dagsschema för måndag-fredag",
          callback: (dayName: WeekDayName) =>
            setDaysFrom("närvaro", dayName, [WeekDayName.monday, WeekDayName.tuesday, WeekDayName.wednesday, WeekDayName.thursday, WeekDayName.friday]),
        },
        {
          caption: "Applicera detta dagsschema för lördag-söndag",
          callback: (dayName: WeekDayName) => setDaysFrom("närvaro", dayName, [WeekDayName.saturday, WeekDayName.sunday]),
        },
        {
          caption: "Applicera detta dagsschema för måndag-söndag",
          callback: (dayName: WeekDayName) =>
            setDaysFrom("närvaro", dayName, [
              WeekDayName.monday,
              WeekDayName.tuesday,
              WeekDayName.wednesday,
              WeekDayName.thursday,
              WeekDayName.friday,
              WeekDayName.saturday,
              WeekDayName.sunday,
            ]),
        },
      ],
    },
  ];

  const frmTidsKanaler = formHook.watch("tidsKanaler");
  const ventilationStoppadPeriodvis = formHook.watch("ventilationStoppadPeriodvis");
  //data[nameof<IModuleVentilationFTXochFTConfig>("ärHuvudsakligtVärmesystem")]
  const ärHuvudsakligtVärmesystem = moduleData.ärHuvudsakligtVärmesystem;

  //moduleData
  const reduceratFlödeRumsdonFasta = moduleData.harReduceradeLuftflödeFastaRumsdon === "Ja" && moduleData.reduceratFlödeRumsdonFasta ? moduleData.reduceratFlödeRumsdonFasta : 0;
  const reduceratFlödeRumsdonReglerbara =
    moduleData.harReduceradeLuftflödeReglerbaraRumsdon === "Ja" && moduleData.reduceratFlödeRumsdonReglerbara ? moduleData.reduceratFlödeRumsdonReglerbara : 0;
  const fxJ3FlödeDelfart = Math.max(reduceratFlödeRumsdonFasta, reduceratFlödeRumsdonReglerbara) / 100;
  const använderReduceratFlöde = fxJ3FlödeDelfart > 0;

  return (
    <div>
      <div className="row ml-0 align-items-end" key={"d3"}>
        {ärHuvudsakligtVärmesystem === "Ja" && (
          <div className="row ml-0 align-items-end mb-2">
            <div className="col-lg-12">
              <StaticBox label="Är ventilationsaggregatet huvudsaklig värmekälla?" text={ärHuvudsakligtVärmesystem}></StaticBox>
            </div>
          </div>
        )}
        <div className="row ml-0 align-items-end">
          <div className="col-lg-4">
            <NumberEditor
              label="Temperatur uteluft (°C)"
              fieldName={nameof<IModuleVentilationFTXochFTRound>("tempUteluft")}
              tooltipId={nameof<MapTooltip>("VentilationFTXoFT_tempUteluft")}
              formHook={formHook}
              min={-30}
              max={30}
              step={0.1}
              options={{ required: "Måste fyllas i" }}
            />
          </div>
        </div>
        <div className="row ml-0 align-items-end">
          {isFTX ? (
            <div className="col-lg-4">
              <NumberEditor
                label="Temperatur tilluft, mätpunkt direkt efter återvinningsbatteri (°C)"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("tempTillLuftEfterÅtervinningsBat")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_tempTillLuftEfterÅtervinningsBat")}
                formHook={formHook}
                min={-30}
                max={30}
                step={0.1}
                options={{ required: "Måste fyllas i" }}
              />
            </div>
          ) : (
            <></>
          )}

          <div className="col-lg-4">
            <NumberEditor
              label="Temperatur tilluft, mätpunkt direkt efter eftervärmningsbatteri (°C)"
              fieldName={nameof<IModuleVentilationFTXochFTRound>("tempTillLuftEfterEftervärmningssBat")}
              tooltipId={nameof<MapTooltip>("VentilationFTXoFT_tempTillLuftEfterEftervärmningssBat")}
              formHook={formHook}
              min={-30}
              max={30}
              step={0.1}
              options={{ required: "Måste fyllas i" }}
            />
          </div>
          {hasKyla ? (
            <div className="col-lg-4">
              <NumberEditor
                label="Temperatur tilluft, mätpunkt direkt efter efterkylningsbatteri (°C)"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("tempTillLuftEfterEfterkylningssBat")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_tempTillLuftEfterEfterkylningssBat")}
                formHook={formHook}
                min={-30}
                max={30}
                step={0.1}
                options={{ required: "Måste fyllas i" }}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
        {isFTX && (
          <div className="row ml-0 align-items-end">
            <div className="col-lg-4">
              <NumberEditor
                label="Temperatur frånluft, mätpunkt direkt innan återvinningsbatteri (°C)"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("tempFrånLuftInnanÅtervinningsBat")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_tempFrånLuftInnanÅtervinningsBat")}
                formHook={formHook}
                min={-30}
                max={30}
                step={0.1}
                options={{ required: "Måste fyllas i" }}
              />
            </div>
            <div className="col-lg-4">
              <NumberEditor
                label="Temperatur avluft, mätpunkt utlopp från ventilationsaggregat (°C)"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("tempAvluftInnanVentilationsAgg")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_tempAvluftInnanVentilationsAgg")}
                formHook={formHook}
                min={-30}
                max={30}
                step={0.1}
                options={{ required: "Måste fyllas i" }}
              />
            </div>
          </div>
        )}
        <div className="row ml-0 align-items-end">
          <div className="col-lg-4 ">
            <NumberEditor
              label="Börvärde regulator tilluft (°C) "
              fieldName={nameof<IModuleVentilationFTXochFTRound>("börvärdeRegTilluft")}
              tooltipId={nameof<MapTooltip>("VentilationFTXoFT_börvärdeRegTilluft")}
              formHook={formHook}
              min={-30}
              max={30}
              step={0.1}
              options={{ required: "Måste fyllas i" }}
            />
          </div>
          <div className="col-lg-4">
            <NumberEditor
              label="Ärvärde regulator tilluft (°C)"
              fieldName={nameof<IModuleVentilationFTXochFTRound>("ärvärdeRegTilluft")}
              tooltipId={nameof<MapTooltip>("VentilationFTXoFT_ärvärdeRegTilluft")}
              formHook={formHook}
              min={-30}
              max={30}
              step={0.1}
              options={{ required: "Måste fyllas i" }}
            />
          </div>
          <div className="col-lg-4">
            <NumberEditor
              label="Kontrollmät temperatur vid mätpunkt för regulator tilluft (°C)"
              fieldName={nameof<IModuleVentilationFTXochFTRound>("kontrollmättTempRegTilluft")}
              tooltipId={nameof<MapTooltip>("VentilationFTXoFT_kontrollmättTempRegTilluft")}
              formHook={formHook}
              min={-30}
              max={30}
              step={0.1}
              options={{ required: "Måste fyllas i" }}
            />
          </div>
        </div>
        <div className="row ml-0 align-items-end">
          <div className="col-lg-4">
            <SelectEditor
              label="Reglertyp för tilluftstemperatur"
              fieldName={nameof<IModuleVentilationFTXochFTRound>("reglerTyp")}
              tooltipId={nameof<MapTooltip>("VentilationFTXoFT_reglerTyp")}
              tooltipClass="wideTooltip"
              formHook={formHook}
              options={{ required: "Måste göra ett val" }}
              items={["", "Tilluftsreglering", "Frånluft eller rumsreglering", "Utomhuskompenserad tilluftsreglering", "Utomhuskompenserad frånluftsreglering"]}
            />
          </div>
        </div>
        {/********************  Flöde ventilationsaggregat  */}
        <div className="row ml-0 mt-4 align-items-end">
          <h5>Flöde ventilationsaggregat</h5>
        </div>
        <div className="row ml-0 align-items-end">
          <div className="col-lg-12">
            <SelectEditor
              label="Uppgift om luftflöden"
              fieldName={nameof<IModuleVentilationFTXochFTRound>("flödeUppgift")}
              tooltipId={nameof<MapTooltip>("VentilationFTXoFT_flödeUppgift")}
              items={luftflödesUppgiftList}
              formHook={formHook}
            />
          </div>
        </div>

        {flödeUppgiftAsSchablon ? (
          <div className="row ml-0 align-items-end">
            <div className="col-lg-6">
              <SelectEditor
                label="Ange form på luftkanaler"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("flödesKanalForm")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_flödesKanalForm")}
                items={["", "Cirkulära", "Rektangulära"]}
                formHook={formHook}
                options={{
                  validate: {
                    required: (v) => {
                      return !v ? "Måste göra ett val" : undefined;
                    },
                  },
                }}
              />
            </div>
            {flödesKanalForm === "Cirkulära" ? (
              <div className="col-lg-6">
                <SelectEditor
                  label="Ange kanalstorlek (cirkulära)"
                  fieldName={nameof<IModuleVentilationFTXochFTRound>("flödeFrånluftDiameter")}
                  tooltipId={nameof<MapTooltip>("VentilationFTXoFT_flödeFrånluftDiameter")}
                  items={cirkStorlekList}
                  formHook={formHook}
                />
              </div>
            ) : (
              <div className="col-lg-6">
                <SelectEditor
                  label="Ange kanalstorlek (rektangulära)"
                  fieldName={nameof<IModuleVentilationFTXochFTRound>("flödeKanalstorlekBxH")}
                  tooltipId={nameof<MapTooltip>("VentilationFTXoFT_flödeKanalstorlekBxH")}
                  items={rektStorlekList}
                  formHook={formHook}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="row ml-0 align-items-end">
            <div className="col-lg-4">
              <SelectEditor
                label="Enhet för luftflöden"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("flödeEnhet")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_flödeEnhet")}
                items={luftflödesEnhetList}
                formHook={formHook}
              />
            </div>
            <div className="col-lg-4">
              <NumberEditor
                label="Flöde tilluft"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("flödeTilluft")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_flödeTilluft")}
                formHook={formHook}
                options={{
                  validate: {
                    required: (v) => {
                      return !v ? "Måste fyllas i" : undefined;
                    },
                    negativeNumber: (value: any) => (parseFloat(value) <= 0 ? "Måste vara positivt värde" : undefined),
                  },
                }}
              />
            </div>
            <div className="col-lg-4">
              <NumberEditor
                label="Flöde frånluft"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("flödeFrånluft")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_flödeFrånluft")}
                formHook={formHook}
                options={{
                  validate: {
                    required: (v) => {
                      return !v ? "Måste fyllas i" : undefined;
                    },
                    negativeNumber: (value: any) => (parseFloat(value) <= 0 ? "Måste vara positivt värde" : undefined),
                  },
                }}
              />
            </div>
          </div>
        )}

        {/********************  Utstyrning temperaturreglering ventilationsaggregat */}
        <div className="row ml-0 mt-4 align-items-end">
          <h5>Utstyrning temperaturreglering ventilationsaggregat </h5>
        </div>
        <div className="row ml-0 align-items-end">
          {isFTX && (
            <div className="col-lg-6">
              <NumberEditor
                label="Utstyrning värmeåtervinning (%) kontrolleras på plats."
                fieldName={nameof<IModuleVentilationFTXochFTRound>("utstyrningVärmeåtervinning")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_utstyrningVärmeåtervinning")}
                formHook={formHook}
                min={0}
                max={100}
                options={{ required: "Måste fyllas i" }}
              />
            </div>
          )}

          <div className="col-lg-6">
            <NumberEditor
              label="Utstyrning eftervärmning (%) kontrolleras på plats"
              fieldName={nameof<IModuleVentilationFTXochFTRound>("utstyrningEftervärmning")}
              tooltipId={nameof<MapTooltip>("VentilationFTXoFT_utstyrningEftervärmning")}
              formHook={formHook}
              min={0}
              max={100}
              options={{ required: "Måste fyllas i" }}
            />
          </div>
        </div>
        {hasKyla ? (
          <div className="row ml-0 align-items-end">
            <div className="col-lg-6">
              <NumberEditor
                label="Utstyrning efterkylning (%) kontrolleras på plats"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("utstyrningEfterkylning")}
                tooltipId={nameof<MapTooltip>("VentilationFTXoFT_utstyrningEfterkylning")}
                formHook={formHook}
                min={0}
                max={100}
                options={{ required: "Måste fyllas i" }}
              />
            </div>
            {isFTX && (
              <div className="col-lg-6">
                <SelectEditor
                  label="Om det finns kylåtervinning, ange om den är aktiv (Ja/Nej)"
                  fieldName={nameof<IModuleVentilationFTXochFTRound>("kylÅtervinningAktiv")}
                  tooltipId={nameof<MapTooltip>("VentilationFTXoFT_kylÅtervinningAktiv")}
                  formHook={formHook}
                  items={["", "Ja", "Nej"]}
                  options={{ required: "Måste göra ett val" }}
                />
              </div>
            )}
          </div>
        ) : (
          <></>
        )}
        {/********************  Tidschema ventilationsaggregat & verksamhet  */}
        <div className="row ml-0 mt-4">
          <h5>Tidschema ventilationsaggregat & verksamhet </h5>
        </div>
        {/* Same as RoundVentilationFX_F. 2 controls for drift året runt and deltidsfaktor. deltidsfaktor only if använderReduceratFlöde  */}
        <div className="row ml-0 align-items-end">
          <div className="col-lg-6">
            <SelectEditor
              label={"Har ventilationsaggregatet dygnet runt drift hela året?"}
              fieldName={nameof<IModuleVentilationFTXochFTRound>("driftÅretRunt")}
              tooltipId={nameof<MapTooltip>("VentilationFXoF_driftÅretRunt")}
              formHook={formHook}
              items={["", "Ja", "Nej"]}
              options={{
                validate: {
                  req: (value) => (value ? undefined : "Ange Ja/Nej"),
                },
              }}
            />
          </div>
          {använderReduceratFlöde ? (
            <div className="col-lg-6">
              <NumberEditor
                label="Delfartsfaktor (%)"
                fieldName={nameof<IModuleVentilationFTXochFTRound>("delfartsFaktor")}
                tooltipId={nameof<MapTooltip>("VentilationFXoF_delfartsFaktor")}
                formHook={formHook}
                isDisabled={driftÅretRunt !== "Ja"}
                options={{
                  required: "Ange andel i procent",
                  validate: {
                    min: (value) => (value >= 0 ? undefined : "Måste vara minst 0"),
                    max: (value) => (value <= 100 ? undefined : "Måste vara högst 100"),
                  },
                }}
              />
            </div>
          ) : (
            <input type="hidden" value={0} {...formHook.register(nameof<IModuleVentilationFTXochFTRound>("delfartsFaktor"))} />
          )}
        </div>
        {/* If drift året runt */}
        {driftÅretRunt === "Nej" ? (
          <>
            <div className="row ml-0 align-items-end">
              <div className="col-lg-4">
                <SelectEditor
                  label="Finns det orsaker till lång drifttid för ventilationsaggregat?"
                  fieldName={nameof<IModuleVentilationFTXochFTRound>("finnsOrsakerTillLångDrifttid")}
                  tooltipId={nameof<MapTooltip>("VentilationFTXoFT_finnsOrsakerTillLångDrifttid")}
                  formHook={formHook}
                  items={["", "Ja", "Nej"]}
                  options={{ required: "Måste göra ett val" }}
                />
              </div>
              <div className="col-lg-4">
                <SelectEditor
                  label="Är nattkyla aktiverad?"
                  fieldName={nameof<IModuleVentilationFTXochFTRound>("nattkylaAktiverad")}
                  tooltipId={nameof<MapTooltip>("VentilationFTXoFT_nattkylaAktiverad")}
                  formHook={formHook}
                  items={["", "Ja", "Nej"]}
                  options={{ required: "Måste göra ett val" }}
                />
              </div>
              <div className="col-lg-4">
                <SelectEditor
                  label="Är ventilationsanläggningen stoppad periodvis under året?"
                  fieldName={nameof<IModuleVentilationFTXochFTRound>("ventilationStoppadPeriodvis")}
                  tooltipId={nameof<MapTooltip>("VentilationFTXoFT_ventilationStoppadPeriodvis")}
                  formHook={formHook}
                  items={["", "Ja", "Nej"]}
                  options={{ required: "Måste göra ett val" }}
                />
              </div>
            </div>
            <div className="row ml-0 align-items-end">
              <div className="col-lg-6">
                <NumberEditor
                  isDisabled={ventilationStoppadPeriodvis !== "Ja"}
                  label="Ange antal veckor som ventilationsanläggningen är stoppad per år"
                  fieldName={nameof<IModuleVentilationFTXochFTRound>("antalVeckorVentilationStoppadPerÅr")}
                  tooltipId={nameof<MapTooltip>("VentilationFTXoFT_antalVeckorVentilationStoppadPerÅr")}
                  formHook={formHook}
                  min={0}
                  max={52}
                  options={{ required: "Måste fyllas i" }}
                />
              </div>
            </div>
            <div className="col-lg-12">
              <TidsKanalEditor data={data} formHook={formHook} tidsKanaler={tidsKanaler}></TidsKanalEditor>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
      <BFLNote formHook={formHook} />
    </div>
  );
};

export default RoundVentilationFTX;
